import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import CookiesBar from '../components/cookiesBar';

import {
  productsTransformer,
  skuProductsTransformer,
  customSkuProductTransformer,
} from '../lib/productTransformer';
import categoryTransformer from '../lib/categoryTransformer';
import AllowCookiesCookie from '../lib/allowCookies';
import { hasValidProductOptions } from '../lib/product';

const DataContext = createContext();

export const DataContextProvider = props => {
  /**
   * COUNTRY CODE
   */
  const [countryCode] = useState(props.countryCode);

  /**
   * ALLOW COOKIES
   */
  const [areCookiesAllowed, setAreCookiesAllowed] = useState(true);

  /**
   * SKU PRODUCTS
   */
  const [skuProducts] = useState(
    skuProductsTransformer(props.productsSpecification, props.skuProducts),
  );
  const indexSkuProducts = useMemo(
    () =>
      skuProducts.reduce((acc, skuProduct, index) => {
        if (hasValidProductOptions(skuProduct)) {
          acc[skuProduct.id] = index;
        }
        return acc;
      }, {}),
    [skuProducts],
  );

  /**
   * CLIENT PRODUCTS
   */
  const [products] = useState(
    productsTransformer(props.productsSpecification, skuProducts, props.productsData),
  );
  const indexProducts = useMemo(
    () =>
      products.reduce((acc, product, index) => {
        acc[product.id] = index;
        return acc;
      }, {}),
    [products],
  );

  /**
   * CLIENT CATEGORIES
   */
  const [categories] = useState(
    categoryTransformer(props.categoriesSpecification, products, props.categoriesData),
  );
  const indexCategories = useMemo(
    () =>
      categories.reduce((acc, category, index) => {
        acc[category.id] = index;
        return acc;
      }, {}),
    [categories],
  );

  useEffect(() => {
    if (AllowCookiesCookie.isConfirmationRequired(props.realCountryCode)) {
      setAreCookiesAllowed(AllowCookiesCookie.get());
    }
  }, [props.realCountryCode]);

  const handleOnAllowCookies = () => {
    setAreCookiesAllowed(true);
    AllowCookiesCookie.set(true);
  };

  const getSkuProduct = (sku, opts = {}) => {
    if (sku in indexSkuProducts) {
      return customSkuProductTransformer(skuProducts[indexSkuProducts[sku]], opts);
    }
    return null;
  };

  const getSkuProducts = (skus = [], opts = {}) => {
    return skus.reduce((acc, sku) => {
      if (sku in indexSkuProducts) {
        acc.push(customSkuProductTransformer(skuProducts[indexSkuProducts[sku]], opts));
      }
      return acc;
    }, []);
  };

  const getProduct = (id, defaultValue = null) => {
    if (id in indexProducts) {
      return products[indexProducts[id]];
    }
    return defaultValue;
  };

  const getProducts = (ids = []) => {
    return ids.reduce((acc, id) => {
      if (id in indexProducts) {
        acc.push(products[indexProducts[id]]);
      }
      return acc;
    }, []);
  };

  const getCategory = id => {
    if (id in indexCategories) {
      return categories[indexCategories[id]];
    }
    return null;
  };

  const getCategories = (ids = []) => {
    return ids.reduce((acc, id) => {
      if (id in indexCategories) {
        acc.push(categories[indexCategories[id]]);
      }
      return acc;
    }, []);
  };

  const getPrice = sku => {
    const skuProduct = getSkuProduct(sku);
    if (skuProduct) {
      return { price: skuProduct.price, currency: skuProduct.currency };
    }
    return null;
  };

  const getProductBySku = sku => {
    return products.find(product =>
      product.options.find(option => option.products.primary.indexOf(sku) >= 0),
    );
  };

  const getCountry = countryCodeValue => {
    return props.countries.find(
      c => c.alpha3 === countryCodeValue || c.alpha2 === countryCodeValue,
    );
  };

  const getCountryAlpha2 = (countryCodeValue, defaultCountryCode = 'US') => {
    const country = getCountry(countryCodeValue);
    if (country) {
      return country.alpha2;
    }

    return defaultCountryCode;
  };

  return (
    <DataContext.Provider
      value={{
        products,
        skuProducts,
        categories,
        countries: props.countries,
        banner: props.banner,
        getSkuProduct,
        getSkuProducts,
        getProduct,
        getProducts,
        getProductBySku,
        getCategory,
        getCategories,
        getPrice,
        getCountry,
        getCountryAlpha2,
        countryCode,
      }}
    >
      {props.children}

      <CookiesBar allowCookies={areCookiesAllowed} onAllowCookies={handleOnAllowCookies} />
    </DataContext.Provider>
  );
};

DataContextProvider.propTypes = {
  countryCode: PropTypes.string,
  realCountryCode: PropTypes.string,
  productsSpecification: PropTypes.any,
  categoriesSpecification: PropTypes.any,
  skuProducts: PropTypes.array,
  productsData: PropTypes.array,
  categoriesData: PropTypes.array,
  countries: PropTypes.array,
  banner: PropTypes.object,
  children: PropTypes.node,
};

const useDataContext = () => {
  return useContext(DataContext);
};

export default useDataContext;
